import { AppConfigService } from '../../core/service/app-config.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { NGXLogger } from 'ngx-logger';
import { User } from '../model/user';
import { AuthenticationService } from '../../core/authentication/authentication.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { UserListService } from 'src/app/core/service/grid-data/grid-data-user.service';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/core/service/user.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import copyToClipboard from 'copy-to-clipboard';
import { Organization } from 'src/app/core/model/organization';
import { sortBy } from 'lodash';

/**
 * List Users
 */
@Component({
  standalone: false,
  selector: 'app-userlist',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss']
})
export class UserListComponent implements OnInit, OnDestroy
{
  /**
   * Toggles the delete confirmation dialog.
   */
  public deleteDialogOpened = false;
  public pageSizes = [5, 10, 25, 50, 100];
  public resendDialogOpened = false;
  public onlineUsersDialogOpened = false;
  public selectedUser: User = null;
  public enableAdvanced = false;
  public count: number;
  public editUser = false;
  public userId: string;
  public isNew = false;
  public roles: Array<{ text: string; value: string }>;
  public filterText = '';
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'name', dir: 'asc' }
    ]
  };

  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public userRoles: Array<{ text: string; value: string }>;
  public loadingOnlineUsers = false;

  public casesTasks: string;
  public channelsPrograms: string;
  public casesTasksChannelsPrograms: string;
  public userInUse: string;
  public deleted: string;

  /**
   * Program lead role?
   */
  public isProgramLeadRole = false;
  public isProgramSupportRole = false;
  public currentUserOrganization: Organization;
  public revenueManager = false;

  private listSubscription: Subscription;

  constructor(
    private userService: UserService,
    private userListService: UserListService,
    private authenticationService: AuthenticationService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private translate: TranslateService,
    private toastrService: ToastrService)
  {
  }

  public get loading(): boolean
  {
    return this.userListService.loading;
  }

  public ngOnInit(): void
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.userRoles = sortBy(this.userService.roles, ['text']);

    this.userService.getUserById(this.authenticationService.currentUser.id, false).subscribe({
      next: (user) =>
      {
        this.isProgramLeadRole = user.role === 'ROLE_PROGRAM_LEAD';
        this.isProgramSupportRole = user.role === 'ROLE_PROGRAM_SUPPORT';
        this.revenueManager = user.role === 'ROLE_REVENUE_MANAGER_OPERATOR';
        this.currentUserOrganization = user.organization;

        this.listSubscription = this.userListService.subscribe({
          next: (value: any) =>
          {
            this.roles = this.userService.roles;
            if (value !== null)
            {
              value.data.forEach(d =>
              {
                d.roleName = this.getRoleDisplayText(d);
                if (d.servicesAccount)
                {
                  d.name = d.username;
                }
                d.lastLoggedOn = d.lastLoggedOn !== null ? new Date(d.lastLoggedOn) : null;
                d.createdOn = d.createdOn !== null ? new Date(d.createdOn) : null;
                d.lastModifiedOn = d.lastModifiedOn !== null ? new Date(d.lastModifiedOn) : null;
                d.allowedRolesText = d.allowedRoles && d.allowedRoles.length > 0 ?
                  this.userService.convertRolesToDisplayTexts(d.allowedRoles.join(',')) : '';
              });
              this.gridView = {
                data: (value) ? this.gridData = value.data : this.gridData = [],
                total: (value) ? this.total = value.total : this.total = 0
              };
            }
          }
        });
        this.getUsers();
      }
    });

    this.translate.get(['USER-IN-USE-CASES-TASKS-CANNOT-DELETE', 'USER-IN-USE-CHANNELS-PROGRAMS-CANNOT-DELETE',
      'USER-IN-USE-CASES-TASKS-CHANNELS-PROGRAMS-CANNOT-DELETE', 'USER-IN-USE-CANNOT-DELETE', 'DELETED']).subscribe({
        next: (res) =>
        {
          this.casesTasks = res['USER-IN-USE-CASES-TASKS-CANNOT-DELETE'];
          this.channelsPrograms = res['USER-IN-USE-CHANNELS-PROGRAMS-CANNOT-DELETE'];
          this.casesTasksChannelsPrograms = res['USER-IN-USE-CASES-TASKS-CHANNELS-PROGRAMS-CANNOT-DELETE'];
          this.userInUse = res['USER-IN-USE-CANNOT-DELETE'];
          this.deleted = res.DELETED.toLowerCase();
        }
      });
  }

  public onlineUsersLoadingChange(loading: boolean): void
  {
    this.loadingOnlineUsers = loading;
  }

  public showOnlineUsers(): void
  {
    this.onlineUsersDialogOpened = true;
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.selectedUser = null;
    this.editUser = true;
  }

  public editHandler(user): void
  {
    this.isNew = false;
    this.userService.getUserById(user.id, true).subscribe({
      next: (data) =>
      {
        this.selectedUser = data;
        this.editUser = true;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      const userId = this.selectedUser.id;
      if (this.selectedUser.username !== undefined)
      {
        this.userService.userInUseCounts(userId).subscribe({
          next: (data) =>
          {
            if ((data.cases > 0 || data.tasks > 0) && (data.channels === 0))
            {
              this.messageHandlerService.displayError(this.casesTasks.replace('{{action}}', this.deleted));
            }
            else
              if (data.channels > 0)
              {
                this.userService.getProgramsChannelsPerUser(userId).subscribe({
                  next: (channels) =>
                  {
                    let channelsText = '';
                    channels.forEach(c => channelsText += c + '<br/>');
                    let messageText = '';
                    if (data.cases > 0 || data.tasks > 0)
                    {
                      messageText = this.casesTasksChannelsPrograms.replace('{{channelsList}}', channelsText).
                        replace('{{action}}', this.deleted);
                    }
                    else
                    {
                      messageText = this.channelsPrograms.replace('{{channelsList}}', channelsText).replace('{{action}}', this.deleted);
                    }
                    this.toastrService.error(messageText, this.userInUse.replace('{{action}}', this.deleted),
                      {
                        enableHtml: true,
                        messageClass: 'channel-message'
                      });
                    this.copyChannelsList(channelsText.replace(/<br\/>/g, '\r\n'));

                  },
                  error: (errorResponse) =>
                  {
                    this.messageHandlerService.displayError(errorResponse);
                  }
                });
              }
              else
              {
                this.userService.deleteUser(userId).subscribe({
                  next: () =>
                  {
                    this.getUsers();
                    this.messageHandlerService.displaySuccess('USER-DELETED-SUCCESSFULLY');
                  },
                  error: (error) =>
                  {
                    this.logger.error(error);
                    this.messageHandlerService.displayError(error);
                  }
                });
              }
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      }
      else
      {
        this.userService.deleteUser(userId).subscribe({
          next: () =>
          {
            this.getUsers();
            this.messageHandlerService.displaySuccess('USER-DELETED-SUCCESSFULLY');
          },
          error: (error) =>
          {
            this.logger.error(error);
            this.messageHandlerService.displayError(error);
          }
        });
      }
    }
    // Close dialog
    this.close('deleteDialog');
    this.selectedUser = null;
  }

  public copyChannelsList(channelsText: string): void
  {
    copyToClipboard(channelsText);
  }

  /**
   * Button action handler for resend registration
   *
   * @param confirm Confirm the action
   */
  public resendAction(confirm: boolean): void
  {
    if (confirm)
    {
      const userId = this.selectedUser.id;
      this.userService.resendRegistrationEmail(userId).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('REGISTRATION-EMAIL-SENT');
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
    // Close dialog
    this.close('resendDialog');
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.getUsers();
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(user): void
  {
    const currentUser = this.authenticationService.currentUser;
    const isCurrentLoggedInUser = (currentUser.userName === user.username);

    if (isCurrentLoggedInUser)
    {
      this.messageHandlerService.displayWarning('CANNOT-DELETE-CURRENT-USER');
    }
    else
    {
      this.userService.getUserById(user.id, false).subscribe({
        next: (data) =>
        {
          this.selectedUser = data;
          this.open('deleteDialog');
        },
        error: (error) =>
        {
          this.messageHandlerService.displayError(error);
        }
      });
    }
  }

  /**
   * Check if resend registration button is visible.
   *
   * @param dataItem Current dataItem row
   * @returns boolean Return true if username is null therefore registration email can be resend.
   */
  public canResendRegistration(dataItem: any): boolean
  {
    return !dataItem.username || dataItem.inRegistration;
  }

  /**
   * User Registration Resend Handler.
   *
   * @param id User id
   */
  public resendRegistrationHandler(id: number): void
  {
    this.userService.getUserById(id, false).subscribe({
      next: (data) =>
      {
        this.selectedUser = data;
        if (this.canResendRegistration(data))
        {
          this.open('resendDialog');
        }
        else
        {
          this.messageHandlerService.displayWarning('USER-ALREADY-REGISTERED');
        }
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }

  /**
   * Get the Role localized text to display.
   *
   * @param dataItem User row item
   */
  public getRoleDisplayText(dataItem: any): string
  {
    const item = this.roles.find(obj => obj.value === dataItem.role);
    if (item)
    {
      return item.text;
    }
    else
    {
      return '';
    }
  }

  /**
   * Event called after save in order to perform grid refresh.
   */
  public afterSaveHandler(): void
  {
    this.getUsers();
    this.editUser = null;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.getUsers();
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  private getUsers(): void 
  {
    if (this.isProgramLeadRole || this.isProgramSupportRole) 
    {
      if (this.state.filter.filters.findIndex((x: any) => x.field === 'organizationId') === -1)
      {
        this.state.filter.filters.push({ field: 'organizationId', operator: 'eq', value: this.currentUserOrganization.id });
      }
    }
    if (this.revenueManager)
    {
      this.state.filter.filters.push({ field: 'roleName', operator: 'eq', value: "ROLE_REVENUE_MANAGER_OPERATOR", logic: 'or' });
      this.state.filter.filters.push({ field: 'roleName', operator: 'eq', value: "ROLE_REVENUE_MANAGER_ANALYST", logic: 'or' });
      this.state.filter.filters.push({ field: 'roleName', operator: 'eq', value: "ROLE_CBO_REVENUE_ANALYST", logic: 'or' });
      this.state.filter.filters.push({ field: 'allowedRoles', operator: 'contains', value: "ROLE_REVENUE_MANAGER_OPERATOR", logic: 'or' });
      this.state.filter.filters.push({ field: 'allowedRoles', operator: 'contains', value: "ROLE_REVENUE_MANAGER_ANALYST", logic: 'or' });
      this.state.filter.filters.push({ field: 'allowedRoles', operator: 'contains', value: "ROLE_CBO_REVENUE_ANALYST", logic: 'or' });
    }
    this.userListService.queryByState(this.state);
  }
}
